import React, { useState, useEffect } from 'react';
import { Search, Filter, MessageCircle, User, DollarSign } from 'lucide-react';
import AuthGuard from './AuthGuard';
import './DiscoverAgents.css';

const DiscoverAgents = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [agents, setAgents] = useState([]);
  const [featuredAgents, setFeaturedAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch real agents from API
  const fetchAgents = async () => {
    try {
      setLoading(true);
      
      // Check if we should use mock data (for screenshots/demos)
      const useMockData = window.location.search.includes('mock=true') || 
                         (window.location.hostname === 'localhost' && 
                          localStorage.getItem('useMockAgents') === 'true');
      
      console.log('Mock data detection:', {
        search: window.location.search,
        hostname: window.location.hostname,
        localStorage: localStorage.getItem('useMockAgents'),
        useMockData: useMockData
      });
      
      const mockParam = useMockData ? '&mock=true' : '';
      
          // Add cache-busting parameter to force fresh data
          const response = await fetch(`/api/agents/public?t=${Date.now()}${mockParam}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched agents data:', data.agents);
        console.log('First agent avatar:', data.agents?.[0]?.agent_avatar); // Debug avatar
        console.log('URLinks agent avatar:', data.agents?.find(a => a.name === 'URLinks')?.agent_avatar); // Debug URLinks avatar
        console.log('My Agent data:', data.agents?.find(a => a.name === 'My Agent'));
        console.log('Featured agents:', data.agents?.filter(agent => agent.isFeatured));
        setAgents(data.agents || []);
        setFeaturedAgents(data.agents?.filter(agent => agent.isFeatured) || []);
      } else {
        console.error('Failed to fetch agents');
        setAgents([]);
        setFeaturedAgents([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
      setFeaturedAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Agents' },
    { id: 'writing', name: 'Writing' },
    { id: 'programming', name: 'Programming' },
    { id: 'business', name: 'Business' },
    { id: 'education', name: 'Education' },
    { id: 'design', name: 'Design' },
    { id: 'creative', name: 'Creative' }
  ];

  useEffect(() => {
    fetchAgents();
  }, []);

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || 
                           agent.tags.some(tag => tag === selectedCategory);
    
    return matchesSearch && matchesCategory;
  });

  const handleChatClick = (agent) => {
    // TODO: Implement chat functionality
    console.log('Starting chat with:', agent.name);
    // This will dispatch an event to switch to chat mode
    const event = new CustomEvent('startAgentChat', { detail: { agent } });
    window.dispatchEvent(event);
  };

  if (loading) {
    return (
      <div className="discover-agents">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading agents...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Search Bar - Top right */}
      <div className="search-bar search-bar-mobile">
        <div className="search-container search-container-mobile">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="discover-agents">
        {/* Main Content Area */}
        <div className="content-area">
        {/* Featured Agents Section */}
        {(featuredAgents.length > 0 || agents.length > 0) && (
          <div className="featured-section">
            <h2 className="section-title">Featured Agents</h2>
            <div className="featured-grid">
              {(featuredAgents.length > 0 ? featuredAgents : agents.slice(0, 2)).map(agent => (
                <AgentCard key={agent.id} agent={agent} onChatClick={handleChatClick} />
              ))}
            </div>
          </div>
        )}

        {/* All Agents Section */}
        <div className="all-agents-section">
          <h2 className="section-title">
            All Agents ({filteredAgents.length})
          </h2>
          <div className="agents-grid">
            {filteredAgents.map(agent => (
              <AgentCard key={agent.id} agent={agent} onChatClick={handleChatClick} />
            ))}
          </div>
          {filteredAgents.length === 0 && (
            <div className="no-results">
              <Search size={48} />
              <h3>No agents found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  );
};

const AgentCard = ({ agent, onChatClick }) => {
  const hasOwnerEngagement = agent.owner_engagement_fee_cents && agent.owner_engagement_fee_cents > 0;
  const priceDisplay = hasOwnerEngagement ? `$${(agent.owner_engagement_fee_cents / 100).toFixed(2)} for owner engagement` : 'Free';

  return (
    <AuthGuard>
      <div 
        className="agent-card"
        onClick={() => onChatClick(agent)}
      >
        <div className="agent-header">
          <div className="agent-avatar discover-agent-avatar">
            {agent.agent_avatar ? (
              <img
                src={agent.agent_avatar}
                alt={agent.name}
                className="discover-agent-avatar-img"
              />
            ) : (
              <User size={20} className="discover-agent-avatar-icon" />
            )}
          </div>
          <div className="agent-pricing">
            <span className={`price-tag ${hasOwnerEngagement ? 'paid' : 'free'}`}>
              {priceDisplay}
            </span>
          </div>
        </div>
        
        <div className="agent-content">
          <h3 className="agent-name">{agent.name}</h3>
          <p className="agent-description">{agent.description}</p>
        </div>
        
        <div className="agent-stats" style={{ display: 'none' }}>
          <MessageCircle size={12} />
          <span>{agent.chat_count || 0} chats</span>
          {hasOwnerEngagement && (
            <div className="paid-indicator">
              <DollarSign size={12} />
              <span>Premium</span>
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  );
};

export default DiscoverAgents;
